import { dataServices, OrderData, Promise, services } from '@ambita/ambita-components-core';
import { ambita, CadastreAndNeighborFeatures } from '@ambita/ambita-map';
import { DirectGeometryObject, Feature, FeatureCollection } from 'geojson';
import { getAccessTokenWithBearer } from './queryParams';

const { util } = services;
const { clone, objectToDelimitedString } = util;

const getCadastreFromFeatureProperties = (feature: Feature<DirectGeometryObject>): string => {
  const { properties } = feature;
  return `${properties.kommunenr}/${properties.gardsnr}/${properties.bruksnr}/${properties.festenr}/`
    + `${properties.seksjonsnr}`;
};

/**
 * Create feature collection containing all provided features. All features get compressed.
 * Main cadastre is marked with `mainCadastre` flag in feature properties.
 * @param mainCadastreFeature GeoJSON feature for main cadastre
 * @param neighborFeatureCollection GeoJSON feature collection for neighbors
 * @returns {FeatureCollection} Feature collection.
 */
export const createNeighborListFeatureCollection = (
  mainCadastreFeature: Feature<DirectGeometryObject>,
  neighborFeatureCollection: FeatureCollection<DirectGeometryObject>
): FeatureCollection<DirectGeometryObject> => {
  if (!mainCadastreFeature || !neighborFeatureCollection) {
    throw new TypeError('Missing input parameters');
  }

  const featureCollection = clone(neighborFeatureCollection);
  mainCadastreFeature.properties.mainCadastre = true;
  featureCollection.features.push(mainCadastreFeature);

  return featureCollection;
};

export const getNeighborListOrderDataFromFeatureCollection = (
  cadastreAndNeighbors: CadastreAndNeighborFeatures
): OrderData.Data => {
  if (!cadastreAndNeighbors) {
    throw new TypeError('Missing input parameters');
  }

  return {
    naboeiendom: cadastreAndNeighbors.neighborsFeatureCollection.features.map(getCadastreFromFeatureProperties),
    geojson: createNeighborListFeatureCollection(
      cadastreAndNeighbors.mainCadastreFeature,
      cadastreAndNeighbors.neighborsFeatureCollection
    )
  };
};

export const composeOrderDataForCadastreInput = (orderData: OrderData.Data): Promise<OrderData.Data> => {
  let targetCadastre;

  if (orderData && orderData.realty && orderData.realty.cadastre) {
    orderData.valgt_eiendom = objectToDelimitedString(orderData.realty.cadastre, '/');
    targetCadastre = objectToDelimitedString(orderData.realty.cadastre);
  } else if (orderData && orderData.realty && orderData.realty.cadastreKey) {
    orderData.valgt_eiendom = orderData.realty.cadastreKey;
    targetCadastre = orderData.realty.cadastreKey;
  } else {
    return Promise.reject(Error(`Order data is missing cadastre information`));
  }

  const mapApi = ambita.Api;
  const apiSettings: any = {
    ambitaToken: getAccessTokenWithBearer(),
    restApi: dataServices.config.getConfig().API_HOST
  };

  return mapApi.getCadastreAndBufferFeatures(targetCadastre, apiSettings)
    .then((cadastreAndNeighborsFeatureCollection: CadastreAndNeighborFeatures) => {
      const neighborListOrderData =
        getNeighborListOrderDataFromFeatureCollection(cadastreAndNeighborsFeatureCollection);

      orderData.naboeiendom = neighborListOrderData.naboeiendom;
      orderData.geojson = neighborListOrderData.geojson;

      return orderData;
    });
};
